import {
  LocaleEnum,
  MarketPlace,
  Placement,
  Template,
} from '@amzn/fire-tv-campaign-automation-service';
import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';

interface SelectionType {
  marketplace: MarketPlace;
  template: Template | undefined;
  placement: Placement;
  locale: LocaleEnum;
  setTemplate: Dispatch<SetStateAction<Template>>;
  setMarketplace: Dispatch<SetStateAction<MarketPlace>>;
  setPlacement: Dispatch<SetStateAction<Placement>>;
  setLocale: Dispatch<SetStateAction<LocaleEnum>>;
}

const SelectionContext = createContext<SelectionType>({
  template: CMSConfig?.TEMPLATES?.[0],
  marketplace: CMSConfig?.MARKET_PLACE[0],
  placement: Placement.FEATURE_ROTATOR,
  locale: CMSConfig?.LOCALE[CMSConfig?.MARKET_PLACE[0]][0],
  setTemplate: () => {},
  setMarketplace: () => {},
  setPlacement: () => {},
  setLocale: () => {},
});

/**
 * Hook to provide selection state management
 * ToDo: Add cookie persistence logic here when needed:
 * - Check for the cookie that has the last selected values
 * - If the cookie is not present, use the default values
 * - If the cookie is present, use the values from the cookie
 */
const useSelectionState = () => {
  const [template, setTemplate] = useState<Template>(CMSConfig?.TEMPLATES?.[0]);
  const [marketplace, setMarketplace] = useState<MarketPlace>(CMSConfig?.MARKET_PLACE?.[0]);
  const [placement, setPlacement] = useState<Placement>(Placement.FEATURE_ROTATOR);
  const [locale, setLocale] = useState<LocaleEnum>(
    CMSConfig?.LOCALE[CMSConfig?.MARKET_PLACE[0]][0],
  );

  return useMemo(
    () => ({
      marketplace,
      template,
      placement,
      locale,
      setMarketplace,
      setTemplate,
      setPlacement,
      setLocale,
    }),
    [marketplace, template, placement, locale],
  );
};

const useSelection = (): SelectionType => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionContextProvider');
  }
  return context;
};

export { SelectionContext, useSelection, useSelectionState };
